import { useState, useCallback } from 'react';

export const useInlineEdit = (onSave) => {
  const [editingField, setEditingField] = useState(null); // { productId, fieldName }
  const [editingValue, setEditingValue] = useState('');
  const [editingLoading, setEditingLoading] = useState(false);
  const [editingErrors, setEditingErrors] = useState({});

  const startEdit = useCallback((productId, fieldName, currentValue) => {
    setEditingField({ productId, fieldName });
    setEditingValue(currentValue || '');
    setEditingErrors({});
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingField(null);
    setEditingValue('');
    setEditingErrors({});
    setEditingLoading(false);
  }, []);

  const validateField = useCallback((fieldName, value) => {
    if (!value && value !== 0) {
      return 'שדה חובה';
    }

    switch (fieldName) {
      case 'price_b2c':
      case 'price_b2b':
        const price = parseFloat(value);
        if (isNaN(price) || price < 0) {
          return 'מחיר חייב להיות מספר חיובי';
        }
        break;
      case 'stock_quantity':
      case 'stock_b2c':
      case 'stock_b2b':
        const stock = parseInt(value);
        if (isNaN(stock) || stock < 0) {
          return 'כמות במלאי חייבת להיות מספר חיובי';
        }
        break;
      case 'name':
        if (value.length < 2) {
          return 'שם המוצר חייב להכיל לפחות 2 תווים';
        }
        break;
      case 'sku':
        if (value.length < 3) {
          return 'SKU חייב להכיל לפחות 3 תווים';
        }
        break;
      default:
        break;
    }

    return null;
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editingField) return;

    const { productId, fieldName } = editingField;
    
    try {
      setEditingLoading(true);
      setEditingErrors({});

      // Validate the input
      const validationError = validateField(fieldName, editingValue);
      if (validationError) {
        setEditingErrors({ [fieldName]: validationError });
        setEditingLoading(false);
        return;
      }

      // Prepare the update data
      const updateData = {};
      
      // Handle special field mappings
      if (fieldName === 'status') {
        updateData.is_active = editingValue === 'active';
      } else if (fieldName === 'category') {
        updateData.category_id = editingValue;
      } else if (fieldName === 'price_b2c' || fieldName === 'price_b2b') {
        updateData[fieldName] = parseFloat(editingValue);
      } else if (fieldName === 'stock_quantity' || fieldName === 'stock_b2c' || fieldName === 'stock_b2b') {
        updateData[fieldName] = parseInt(editingValue);
      } else {
        updateData[fieldName] = editingValue;
      }

      // Call the save handler
      await onSave(productId, updateData);
      
      // Clear editing state
      cancelEdit();
      
    } catch (error) {
      setEditingErrors({ 
        [fieldName]: error.response?.data?.message || 'שגיאה בעדכון השדה' 
      });
    } finally {
      setEditingLoading(false);
    }
  }, [editingField, editingValue, onSave, cancelEdit, validateField]);

  const isEditing = useCallback((productId, fieldName) => {
    return editingField?.productId === productId && editingField?.fieldName === fieldName;
  }, [editingField]);

  return {
    editingField,
    editingValue,
    editingLoading,
    editingErrors,
    setEditingValue,
    startEdit,
    cancelEdit,
    saveEdit,
    isEditing
  };
};

